/**
 * useDrafts Hook
 * Manages draft lifecycle for the discharge feature.
 * Separate from useDischargeQueue which manages individual queue items.
 *
 * @param {Object} [options]
 * @param {number} [options.agencyId] - Current agency (filters drafts displayed)
 */

import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getDrafts,
  getDraft,
  createDraft as createDraftApi,
  abandonDraft as abandonDraftApi,
} from '../services/oathDischargeService';

export const useDrafts = ({ agencyId } = {}) => {
  // Draft list state
  const [drafts, setDrafts] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);

  // Active draft state (null = working with unassigned queue items)
  const [activeReportId, setActiveReportId] = useState(null);
  const [activeDraftMeta, setActiveDraftMeta] = useState(null);

  // ============================================
  // FETCH DRAFTS (on mount and when agencyId changes)
  // ============================================

  const refreshDrafts = useCallback(async () => {
    if (!agencyId) return;
    setIsLoadingDrafts(true);
    try {
      const result = await getDrafts();
      if (result.success) {
        // Filter to current agency client-side
        const filtered = (result.data || []).filter(
          (d) => String(d.agentId) === String(agencyId),
        );
        setDrafts(filtered);
      }
    } catch (err) {
      console.error('Refresh drafts error:', err);
    } finally {
      setIsLoadingDrafts(false);
    }
  }, [agencyId]);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  // ============================================
  // CREATE DRAFT
  // ============================================

  const handleCreateDraft = useCallback(async () => {
    if (!agencyId) {
      toast.error('Please select an agency first.');
      return null;
    }
    try {
      const result = await createDraftApi(agencyId);
      if (result.success && result.data) {
        const draft = result.data;
        setActiveReportId(draft.reportId);
        setActiveDraftMeta(draft);
        await refreshDrafts();
        toast.success(draft.message || 'Draft saved successfully.');
        return draft;
      } else {
        toast.error(result.error || 'Failed to create draft.');
        return null;
      }
    } catch (err) {
      console.error('Create draft error:', err);
      toast.error('An error occurred while creating the draft.');
      return null;
    }
  }, [agencyId, refreshDrafts]);

  // ============================================
  // RESUME DRAFT
  // ============================================

  const handleResumeDraft = useCallback(async (reportId) => {
    try {
      const result = await getDraft(reportId);
      if (result.success && result.data) {
        setActiveReportId(reportId);
        setActiveDraftMeta(result.data);
        return result.data;
      } else {
        toast.error(result.error || 'Failed to load draft.');
        return null;
      }
    } catch (err) {
      console.error('Resume draft error:', err);
      toast.error('An error occurred while loading the draft.');
      return null;
    }
  }, []);

  // ============================================
  // ABANDON DRAFT
  // ============================================

  const handleAbandonDraft = useCallback(async (reportId) => {
    const target = reportId || activeReportId;
    if (!target) return false;
    try {
      const result = await abandonDraftApi(target);
      if (result.success) {
        if (target === activeReportId) {
          setActiveReportId(null);
          setActiveDraftMeta(null);
        }
        await refreshDrafts();
        toast.success('Draft abandoned.');
        return true;
      } else {
        toast.error(result.error || 'Failed to abandon draft.');
        return false;
      }
    } catch (err) {
      console.error('Abandon draft error:', err);
      toast.error('An error occurred while abandoning the draft.');
      return false;
    }
  }, [activeReportId, refreshDrafts]);

  // ============================================
  // EXIT DRAFT MODE (return to active queue)
  // ============================================

  const exitDraft = useCallback(() => {
    setActiveReportId(null);
    setActiveDraftMeta(null);
  }, []);

  return {
    drafts,
    isLoadingDrafts,
    activeReportId,
    activeDraftMeta,
    createDraft: handleCreateDraft,
    resumeDraft: handleResumeDraft,
    abandonDraft: handleAbandonDraft,
    exitDraft,
    refreshDrafts,
  };
};

export default useDrafts;
